import React, { useState, useEffect, useMemo, useRef } from 'react'
import moment from 'moment-timezone'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import ButtonSimple from '~/Common-Objects/UI/Forms/ButtonSimple/ButtonSimple'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import { useSelector } from 'react-redux'
import i18n from '~/i18n'
import {
    getCalendarBounds,
    shiftCalendarCursor,
    getMonthsInView,
    formatCalendarTitle,
    indexEventsByDay,
    buildMonthWeeks,
    CALENDAR_RANGE_VIEWS
} from '~/utils/calendarRange'

const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6]

function EventRow({ event, dayDate, userTimezone, onClick }) {
    const eventStart = moment.utc(event.start).tz(userTimezone)
    const eventEnd = event.end ? moment.utc(event.end).tz(userTimezone) : null
    const startsBefore = eventStart.clone().startOf('day') < moment(dayDate).clone().startOf('day')
    const endsAfter = eventEnd && eventEnd.clone().endOf('day') > moment(dayDate).clone().endOf('day')
    return (
        <div
            key={'event-' + event.id}
            className={event.classes}
            onClick={(e) => {
                e.stopPropagation()
                onClick(event)
            }}
        >
            {startsBefore && <div className='icon icon-chevron-left' />}
            <div className='name'>
                <div>{event.title}</div>
                {event.iconRight}
            </div>
            {endsAfter && <div className='icon icon-chevron-right' />}
        </div>
    )
}

function MonthGrid({
    monthMoment,
    eventsByDay,
    userTimezone,
    language,
    loading,
    addEvent,
    clickEvent,
    compact,
    onSelectMonth,
    showWeekdayHeaders,
    showMonthTitle
}) {
    const weeks = useMemo(
        () => buildMonthWeeks(monthMoment, eventsByDay, userTimezone),
        [monthMoment, eventsByDay, userTimezone]
    )

    return (
        <div className={'monthGrid' + (compact ? ' compact-' + compact : '')}>
            {Boolean(showMonthTitle) && (
                <div
                    className={'monthTitle' + (onSelectMonth ? ' clickable' : '')}
                    onClick={() => onSelectMonth && onSelectMonth(monthMoment.clone().startOf('month'))}
                >
                    {monthMoment.clone().locale(language).format(compact === 'year' ? 'MMM' : 'MMMM')}
                </div>
            )}
            {Boolean(showWeekdayHeaders) && (
                <div className='weekdayHeaders hide-xs'>
                    {DAYS_OF_WEEK.map((day) => (
                        <div
                            key={'day_' + monthMoment.format('YYYY-MM') + '_' + day}
                            className='weekdayHeader'
                            style={{
                                fontSize: compact === 'year' ? 11 : 12,
                                fontWeight: 600,
                                marginBottom: 8,
                                paddingLeft: compact ? 4 : 12
                            }}
                        >
                            {moment
                                .tz(userTimezone)
                                .locale(language)
                                .weekday(day)
                                .format(compact ? 'dd' : 'dddd')}
                        </div>
                    ))}
                </div>
            )}
            <div className='weeks'>
                {weeks.map((week, weekIndex) =>
                    DAYS_OF_WEEK.map((day) => {
                        const isToday = week[day] && moment.tz(userTimezone).isSame(week[day].date, 'day')
                        const isWeekend = week[day] && (week[day].date.day() === 0 || week[day].date.day() === 6)
                        let className = 'day'
                        if (week[day]) {
                            if (weekIndex === 0) className += ' noBottom borTop'
                            if (day === 0 || week[day].dateStr === '1') className += ' borLeft'
                            if (week[day] && (week[day].dateStr < 8 || weekIndex === 1)) className += ' borTop'
                            if (weekIndex === weeks.length - 1) className += ' borBottom'
                            if (isToday) className += ' today'
                            if (isWeekend) className += ' weekend'
                        }

                        const entries = week[day] ? week[day].entries : []
                        const yearDots = compact === 'year' ? entries.slice(0, 3) : []
                        const extraCount = compact === 'year' && entries.length > 3 ? entries.length - 3 : 0

                        return (
                            <div
                                key={'week-' + weekIndex + '-day-' + day}
                                className='dayContainer'
                                onClick={() => {
                                    if (compact === 'year' && week[day] && onSelectMonth) {
                                        onSelectMonth(week[day].date.clone().startOf('month'))
                                    }
                                }}
                            >
                                {Boolean(week[day]) && (
                                    <div className={className}>
                                        <div className='dayNumber'>
                                            <div className='num'>{week[day].dateStr}</div>
                                        </div>
                                        {Boolean(entries.length) && compact !== 'year' && (
                                            <div className='dayInfo'>({entries.length})</div>
                                        )}
                                        {Boolean(loading) && <LoadingIcons iconType='2' />}
                                        {!Boolean(loading) && compact !== 'year' && (
                                            <div className='eventContainer'>
                                                {entries.map((e) => (
                                                    <EventRow
                                                        key={'event-' + e.id}
                                                        event={e}
                                                        dayDate={week[day].date}
                                                        userTimezone={userTimezone}
                                                        onClick={clickEvent}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {!Boolean(loading) && compact === 'year' && Boolean(entries.length) && (
                                            <div className='yearDots'>
                                                {yearDots.map((e) => (
                                                    <span
                                                        key={'dot-' + e.id}
                                                        className={'yearDot ' + (e.classes || '')}
                                                    />
                                                ))}
                                                {Boolean(extraCount) && (
                                                    <span className='yearDotExtra'>+{extraCount}</span>
                                                )}
                                            </div>
                                        )}
                                        {!Boolean(loading) && Boolean(addEvent) && compact !== 'year' && (
                                            <div className='addEvent'>
                                                <Button onClick={() => addEvent(week[day].date)}>
                                                    <Icons icon='plus' />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    )
}

export default function Calendar({
    events,
    loading = false,
    addEvent,
    onChangeDate = () => {},
    clickEvent = () => {},
    defaultRangeView = 'month',
    onChangeRangeView = () => {}
}) {
    const user = useSelector((state) => state.user)
    const userTimezone = user.userData.timeZone
    const language = user.userData.language
    const initialView = CALENDAR_RANGE_VIEWS.includes(defaultRangeView) ? defaultRangeView : 'month'
    const [rangeView, setRangeView] = useState(initialView)
    const [cursor, setCursor] = useState(() => {
        const now = moment.tz(userTimezone)
        if (initialView === 'year') return now.startOf('year')
        if (initialView === 'quarter') return now.startOf('quarter')
        return now.startOf('month')
    })
    const skipDateNotify = useRef(true)
    moment.locale(language)

    const eventsByDay = useMemo(() => indexEventsByDay(events, userTimezone), [events, userTimezone])
    const months = useMemo(() => getMonthsInView(cursor, rangeView, userTimezone), [cursor, rangeView, userTimezone])

    useEffect(() => {
        if (skipDateNotify.current) {
            skipDateNotify.current = false
            return
        }
        const bounds = getCalendarBounds(cursor, rangeView, userTimezone)
        onChangeDate(cursor.clone(), { start: bounds.start, end: bounds.end, view: rangeView })
    }, [cursor, rangeView, userTimezone])

    const moveForward = () => {
        setCursor(shiftCalendarCursor(cursor, rangeView, 1, userTimezone))
    }
    const moveBack = () => {
        setCursor(shiftCalendarCursor(cursor, rangeView, -1, userTimezone))
    }

    const changeRangeView = (view) => {
        if (view === rangeView) return
        setRangeView(view)
        setCursor((current) => {
            if (view === 'year') return current.clone().startOf('year')
            if (view === 'quarter') return current.clone().startOf('quarter')
            return current.clone().startOf('month')
        })
        onChangeRangeView(view)
    }

    const selectMonth = (monthDate) => {
        skipDateNotify.current = false
        setCursor(monthDate.clone().startOf('month'))
        setRangeView('month')
        onChangeRangeView('month')
    }

    const compact = rangeView === 'month' ? null : rangeView

    return (
        <div className={'calendar range-' + rangeView}>
            <div className='row mar-b20'>
                <div className='col-xs-12 center-xs'>
                    <div className='quickFlex center-xs text-lg controls'>
                        <Button onClick={moveBack}>
                            <Icons icon='arrowLeft' />
                        </Button>
                        <div style={{ minWidth: 250, fontSize: 20, fontWeight: 600 }}>
                            {formatCalendarTitle(cursor, rangeView, language, userTimezone)}
                        </div>
                        <Button onClick={moveForward}>
                            <Icons icon='arrowRight' />
                        </Button>
                    </div>
                    <div className='quickFlex center-xs rangeViews mar-t10'>
                        {CALENDAR_RANGE_VIEWS.map((view) => (
                            <ButtonSimple
                                key={view}
                                className='button-simple-fullsize mar-r5'
                                selected={rangeView === view}
                                onClick={() => changeRangeView(view)}
                            >
                                {i18n.t('campaigns:calendar.' + view)}
                            </ButtonSimple>
                        ))}
                    </div>
                </div>
            </div>
            {rangeView === 'month' && (
                <MonthGrid
                    monthMoment={cursor}
                    eventsByDay={eventsByDay}
                    userTimezone={userTimezone}
                    language={language}
                    loading={loading}
                    addEvent={addEvent}
                    clickEvent={clickEvent}
                    compact={null}
                    showWeekdayHeaders={true}
                    showMonthTitle={false}
                />
            )}
            {rangeView !== 'month' && (
                <div className='monthsGrid'>
                    {months.map((monthMoment) => (
                        <div key={monthMoment.format('YYYY-MM')} className='miniMonth'>
                            <MonthGrid
                                monthMoment={monthMoment}
                                eventsByDay={eventsByDay}
                                userTimezone={userTimezone}
                                language={language}
                                loading={loading}
                                addEvent={addEvent}
                                clickEvent={clickEvent}
                                compact={compact}
                                onSelectMonth={selectMonth}
                                showWeekdayHeaders={true}
                                showMonthTitle={true}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
